import CatalogEntity from "../Components/CatalogEntity";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField} from "@mui/material";
import {renderColumnValue, renderNonWarpingHeader} from "../../../General/SharedMethods";
import {MRT_EditActionButtons} from "material-react-table";
import * as yup from 'yup';

const AlgoParametersEditor = ({enums}) => {
    const ViewAlgoParametersDialog = (columns, viewRow, setViewRow) => {
        const components = [];
        for (let i = 0; i < columns.length; i += 4) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(4)].map((_, j) => {
                        const column = columns[i + j];
                        return column && (
                            <Grid item xs={12} sm={8} md={3} key={column.accessorKey}>
                                <TextField
                                    label={column.header}
                                    defaultValue={renderColumnValue(column, viewRow, enums)}
                                    InputProps={{readOnly: true}}
                                    variant="standard"
                                    fullWidth
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        return (
            <Dialog maxWidth="xl" open={true}>
                <DialogTitle variant="h4">View Details</DialogTitle>
                <DialogContent>{components}</DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewRow(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const AlgoParametersDialog = (table, row, internalEditComponents, validationErrors, setValidationErrors, dialogType) => {
        const components = [];
        for (let i = 1; i < internalEditComponents.length; i += 4) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(4)].map((_, j) => {
                        const component = internalEditComponents[i + j];
                        return component && (
                            <Grid item xs={12} sm={6} md={3} key={`item-${i}-${j}`}>
                                {component}
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        return (
            <Dialog fullWidth maxWidth="lg" open={true}>
                <DialogTitle variant="h4">{dialogType === "edit" ? "Edit Algo Parameter" : "Create a new Algo Parameter"}</DialogTitle>
                <DialogContent sx={{overflowY: 'auto'}}>{components}</DialogContent>
                <DialogActions>
                    <MRT_EditActionButtons variant="text" table={table} row={row}/>
                </DialogActions>
            </Dialog>
        );
    };

    const createAlgoParametersColumns = (validationErrors, setValidationErrors) => {
        return [
            {
                accessorKey: 'id',
                header: 'Id',
                enableEditing: false,
                muiEditTextFieldProps: {
                    type: 'number',
                    hidden: true,
                },
            },
            {
                accessorKey: 'name',
                header: 'Name',
                muiEditTextFieldProps: {
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.name,
                    helperText: validationErrors?.name,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            name: undefined,
                        }),
                },
            },
            {
                accessorKey: 'pulseBufferSize',
                header: 'PulseBufferSize',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.pulseBufferSize,
                    helperText: validationErrors?.pulseBufferSize,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            pulseBufferSize: undefined,
                        }),
                },
            },
            {
                accessorKey: 'rate1',
                header: 'Rate1',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.rate1,
                    helperText: validationErrors?.rate1,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            rate1: undefined,
                        }),
                },
            },
            {
                accessorKey: 'rate2',
                header: 'Rate2',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.rate2,
                    helperText: validationErrors?.rate2,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            rate2: undefined,
                        }),
                },
            },
            {
                accessorKey: 'rate3',
                header: 'Rate3',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.rate3,
                    helperText: validationErrors?.rate3,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            rate3: undefined,
                        }),
                },
            },
            {
                accessorKey: 'rate4',
                header: 'Rate4',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.rate4,
                    helperText: validationErrors?.rate4,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            rate4: undefined,
                        }),
                },
            },
            {
                accessorKey: 'rate5',
                header: 'Rate5',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.rate5,
                    helperText: validationErrors?.rate5,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            rate5: undefined,
                        }),
                },
            },
            {
                accessorKey: 'max1',
                header: 'Max1',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.max1,
                    helperText: validationErrors?.max1,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            max1: undefined,
                        }),
                },
            },
            {
                accessorKey: 'max2',
                header: 'Max2',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.max2,
                    helperText: validationErrors?.max2,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            max2: undefined,
                        }),
                },
            },
            {
                accessorKey: 'max3',
                header: 'Max3',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.max3,
                    helperText: validationErrors?.max3,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            max3: undefined,
                        }),
                },
            },
            {
                accessorKey: 'max4',
                header: 'Max4',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.max4,
                    helperText: validationErrors?.max4,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            max4: undefined,
                        }),
                },
            },
            {
                accessorKey: 'max5',
                header: 'Max5',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.max5,
                    helperText: validationErrors?.max5,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            max5: undefined,
                        }),
                },
            },
            {
                accessorKey: 'highMaxCumConsumpAwayMax',
                header: renderNonWarpingHeader('High Max Cum Consump Away Max'),
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.highMaxCumConsumpAwayMax,
                    helperText: validationErrors?.highMaxCumConsumpAwayMax,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            highMaxCumConsumpAwayMax: undefined,
                        }),
                },
            },
            {
                accessorKey: 'maxTimeForIrrigStart',
                header: renderNonWarpingHeader('Max Time For Irrig Start'),
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.maxTimeForIrrigStart,
                    helperText: validationErrors?.maxTimeForIrrigStart,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            maxTimeForIrrigStart: undefined,
                        }),
                },
            },
            {
                accessorKey: 'minConsumpRatePulseInterval',
                header: renderNonWarpingHeader('Min Consump Rate Pulse Interval'),
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.minConsumpRatePulseInterval,
                    helperText: validationErrors?.minConsumpRatePulseInterval,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            minConsumpRatePulseInterval: undefined,
                        }),
                },
            },
            {
                accessorKey: 'lowSteadyFlowMinNumPulses',
                header: renderNonWarpingHeader('Low Steady Flow Min Num Pulses'),
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.lowSteadyFlowMinNumPulses,
                    helperText: validationErrors?.lowSteadyFlowMinNumPulses,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            lowSteadyFlowMinNumPulses: undefined,
                        }),
                },
            },
            {
                accessorKey: 'maxBgLeakSize',
                header: 'Max Bg Leak Size',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.maxBgLeakSize,
                    helperText: validationErrors?.maxBgLeakSize,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            maxBgLeakSize: undefined,
                        }),
                },
            },
            {
                accessorKey: 'minTableEvtSize',
                header: 'Min Table Evt Size',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.minTableEvtSize,
                    helperText: validationErrors?.minTableEvtSize,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            minTableEvtSize: undefined,
                        }),
                },
            },
            {
                accessorKey: 'minTableEvtSizeIrrig',
                header: renderNonWarpingHeader('Min Table Evt Size Irrig'),
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.minTableEvtSizeIrrig,
                    helperText: validationErrors?.minTableEvtSizeIrrig,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            minTableEvtSizeIrrig: undefined,
                        }),
                },
            },
            {
                accessorKey: 'maxFlowForValveTest',
                header: renderNonWarpingHeader('Max Flow For Valve Test'),
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.maxFlowForValveTest,
                    helperText: validationErrors?.maxFlowForValveTest,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            maxFlowForValveTest: undefined,
                        }),
                },
            },
            {
                accessorKey: 'highMaxCumConsumpMinNumPulses',
                header: renderNonWarpingHeader('High Max Cum Consump Min Num Pulses'),
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.highMaxCumConsumpMinNumPulses,
                    helperText: validationErrors?.highMaxCumConsumpMinNumPulses,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            highMaxCumConsumpMinNumPulses: undefined,
                        }),
                },
            },
            {
                accessorKey: 'highMaxCumConsumpMinNumPulsesIrrig',
                header: renderNonWarpingHeader('High Max Cum Consump Min Num Pulses Irrig'),
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.highMaxCumConsumpMinNumPulsesIrrig,
                    helperText: validationErrors?.highMaxCumConsumpMinNumPulsesIrrig,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            highMaxCumConsumpMinNumPulsesIrrig: undefined,
                        }),
                },
            },
            {
                accessorKey: 'vmaxConsump',
                header: 'Vmax Consump',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.vmaxConsump,
                    helperText: validationErrors?.vmaxConsump,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            vmaxConsump: undefined,
                        }),
                },
            },
            {
                accessorKey: 'vmaxConsumpIrrig',
                header: 'Vmax Consump Irrig',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.vmaxConsumpIrrig,
                    helperText: validationErrors?.vmaxConsumpIrrig,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            vmaxConsumpIrrig: undefined,
                        }),
                },
            },
        ]
    }

    const algoParameterValidationSchema = yup.object().shape({
        name: yup.string().required('Name is required').max(255, 'Name must be no longer than 255 characters'),
        pulseBufferSize: yup.number().typeError('Pulse Buffer Size is required').required('Pulse Buffer Size is required').min(8).max(512),
        rate1: yup.number().typeError('Rate1 is required').required('Rate1 is required').min(0),
        max1: yup.number().typeError('Max1 is required').required('Max1 is required').min(0).max(65000),
        rate2: yup.number().typeError('Rate2 is required').required('Rate2 is required').min(0)
            .test('is-greater-than-rate1', 'Rate2 must be greater than or equal to Rate1', function (value) {
                const {rate1} = this.parent;
                return value >= rate1;
            }),
        max2: yup.number().typeError('Max2 is required').required('Max2 is required').min(0).max(65000)
            .test('is-greater-than-max1', 'Max2 must be greater than or equal to Max1', function (value) {
                const {max1} = this.parent;
                return value >= max1;
            }),
        rate3: yup.number().typeError('Rate3 is required').required('Rate3 is required').min(0)
            .test('is-greater-than-rate2', 'Rate3 must be greater than or equal to Rate2', function (value) {
                const {rate2} = this.parent;
                return value >= rate2;
            }),
        max3: yup.number().typeError('Max3 is required').required('Max3 is required').min(0).max(65000)
            .test('is-greater-than-max2', 'Max3 must be greater than or equal to Max2', function (value) {
                const {max2} = this.parent;
                return value >= max2;
            }),
        rate4: yup.number().typeError('Rate4 is required').required('Rate4 is required').min(0)
            .test('is-greater-than-rate3', 'Rate4 must be greater than or equal to Rate3', function (value) {
                const {rate3} = this.parent;
                return value >= rate3;
            }),
        max4: yup.number().typeError('Max4 is required').required('Max4 is required').min(0).max(65000)
            .test('is-greater-than-max3', 'Max4 must be greater than or equal to Max3', function (value) {
                const {max3} = this.parent;
                return value >= max3;
            }),
        rate5: yup.number().typeError('Rate5 is required').required('Rate5 is required').min(0)
            .test('is-greater-than-rate4', 'Rate5 must be greater than or equal to Rate4', function (value) {
                const {rate4} = this.parent;
                return value >= rate4;
            }),
        max5: yup.number().typeError('Max5 is required').required('Max5 is required').min(0).max(65000)
            .test('is-greater-than-max4', 'Max5 must be greater than or equal to Max4', function (value) {
                const {max4} = this.parent;
                return value >= max4;
            }),
        highMaxCumConsumpAwayMax: yup.number().typeError('High Max Cum Consump Away Max is required').required('High Max Cum Consump Away Max is required').min(0).max(65000),
        maxTimeForIrrigStart: yup.number().typeError('Max Time For Irrig Start is required').required('Max Time For Irrig Start is required').min(0),
        minConsumpRatePulseInterval: yup.number().typeError('Min Consump Rate Pulse Interval is required').required('Min Consump Rate Pulse Interval is required').min(0),
        lowSteadyFlowMinNumPulses: yup.number().typeError('Low Steady Flow Min Num Pulses is required').required('Low Steady Flow Min Num Pulses is required').min(4).max(65000),
        maxBgLeakSize: yup.number().typeError('Max Bg Leak Size is required').required('Max Bg Leak Size is required').min(1),
        minTableEvtSize: yup.number().typeError('Min Table Evt Size is required').required('Min Table Evt Size is required').min(1),
        minTableEvtSizeIrrig: yup.number().typeError('Min Table Evt Size Irrig is required').required('Min Table Evt Size Irrig is required').min(1),
        maxFlowForValveTest: yup.number().typeError('Max Flow For Valve Test is required').required('Max Flow For Valve Test is required').min(0),
        highMaxCumConsumpMinNumPulses: yup.number().typeError('High Max Cum Consump Min Num Pulses is required').required('High Max Cum Consump Min Num Pulses is required').min(0),
        highMaxCumConsumpMinNumPulsesIrrig: yup.number().typeError('High Max Cum Consump Min Num Pulses Irrig is required').required('High Max Cum Consump Min Num Pulses Irrig is required').min(0),
        vmaxConsump: yup.number().typeError('Vmax Consump is required').required('Vmax Consump is required').min(1).max(500000),
        vmaxConsumpIrrig: yup.number().typeError('Vmax Consump Irrig is required').required('Vmax Consump Irrig is required').min(1).max(500000)

    });

    return <CatalogEntity entityType={'algo-parameters'}
                          entityName={'Algo Parameters'}
                          createColumns={createAlgoParametersColumns}
                          validationSchema={algoParameterValidationSchema}
                          customEditDialog={AlgoParametersDialog}
                          customCreateDialog={AlgoParametersDialog}
                          customViewDialog={ViewAlgoParametersDialog}
                          enums={enums}

    />

}


export default AlgoParametersEditor;
